// @flow
import {
  ArrowLeftUniversal,
  CopyOutlined,
  EditOutlined,
  FolderOutlined,
  MoreVerticalUniversal,
  UploadUniversal,
  StopUniversal,
} from '@duxton-web/icons';
import {
  Button,
  Divider,
  Dropdown,
  Flex,
  Modal,
  Tabs,
  Typography,
} from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import _ from 'lodash';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  GET_UCD_PROJECT,
  GET_UCD_PROJECT_METRICS,
  getUCDConfigs,
  getUCDProject,
  getUCDProjectMetrics,
  UPDATE_UCD_PROJECT_STATUS,
  updateUCDProjectStatus,
} from 'omega-rtc/actions/ucd';
import ProgressiveContainer from 'omega-rtc/components/container/ProgressiveContainer';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import ScribeEventName, { ScribeExtras } from 'omega-rtc/constants/scribe';
import {
  INTERNAL_STATUS,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import {
  hasJustSuccess,
  isRequestsPending,
  isRequestsSuccessful,
} from 'omega-rtc/utils/request-state-utils';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import ProjectPerformancePage from 'omega-rtc/views/ucd/ProjectPerformancePage';

import CampaignSearchView from 'omega-rtc/views/ucd/CampaignSearchView';
import CampaignSearchEmptyView from 'omega-rtc/views/ucd/CampaignSearchEmptyView';
import './ProjectDetail.scss';
import ProjectDetailOverview from 'omega-rtc/views/ucd/ProjectDetailOverview';
import './UCDHomePage.scss';
import CreateCampaignProjectButton from 'omega-rtc/components/custom/CreateCampaignProjectButton';

const TITLE_BAR_MENU_ITEM_KEYS = {
  EDIT: 'edit',
  DUPLICATE: 'duplicate',
  PUBLISH: 'publish',
  DEACTIVATE: 'deactivate',
};

const ProjectDetailPage = (props) => {
  const updateStatusRequestRef = useRef();

  const dispatch = useDispatch();
  const { router, location, params } = props;

  const searchParams = location.query;

  const projectID = useMemo(() => params.entityID, [params]);

  const user = useSelector((state) => state.app.user);
  const [statusRequestType, setStatusRequestType] = useState('');
  const [isPerformanceTabClicked, setIsPerformanceTabClicked] = useState(false);

  const countries = useSelector((state) => state.countries.entities);
  const projectView = useSelector((state) =>
    _.get(state.ucd, 'projectView', { project: {} }),
  );
  const projectMetricsView = useSelector(
    (state) => state.ucd.projectMetricsView,
  );
  const updateProjectStatusRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(UPDATE_UCD_PROJECT_STATUS), {}),
  );
  const getProjectRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(GET_UCD_PROJECT), {}),
  );
  const getProjectMetricsViewRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(GET_UCD_PROJECT_METRICS), {}),
  );

  useEffect(() => {
    dispatch(getUCDConfigs());
    dispatch(getUCDProject(projectID));
    dispatch(getUCDProjectMetrics(projectID));
    updateStatusRequestRef.current = updateProjectStatusRequest;
  }, [dispatch, projectID]);

  useEffect(() => {
    // When requet is pending set the ref to the current request
    if (isRequestsPending(updateProjectStatusRequest)) {
      updateStatusRequestRef.current = updateProjectStatusRequest;
    }
  }, [updateProjectStatusRequest]);

  useEffect(() => {
    if (
      isRequestsSuccessful(updateProjectStatusRequest) &&
      hasJustSuccess(updateStatusRequestRef.current, updateProjectStatusRequest)
    ) {
      snackBar.success({
        content: `${_.get(projectView, 'project.name', 'Project')} has been ${statusRequestType === INTERNAL_STATUS.active
          ? 'activated'
          : 'deactivated'
        } successfully.`,
      });
      // After request is successful, reset the request ref
      updateStatusRequestRef.current = updateProjectStatusRequest;
      dispatch(getUCDProject(projectID));
    }
  }, [updateProjectStatusRequest, dispatch]);

  const handleBackButton = (previousPageIsListing) => {
    if (previousPageIsListing) {
      router.goBack();
    } else {
      router.push(UCD_ROUTES_LINK.HOME());
    }
  };

  const tabs = useMemo(
    () => [
      {
        key: 'overview',
        label: 'Overview',
        children: <ProjectDetailOverview />,
      },
      {
        key: 'campaigns',
        label: 'Campaigns',
        children:
          projectView.campaignCount > 0 ? (
            <CampaignSearchView
              searchParams={searchParams}
              router={router}
              user={user}
              projectID={projectID}
            />
          ) : (
            <CampaignSearchEmptyView router={router} />
          ),
      },
      {
        key: 'performance',
        label: 'Performance',
        children: (
          <ProgressiveContainer
            isLoading={isRequestsPending(getProjectMetricsViewRequest)}
          >
            <ProjectPerformancePage
              projectView={projectView}
              projectMetricsView={projectMetricsView}
              projectID={projectID}
            />
          </ProgressiveContainer>
        ),
      },
    ],
    [
      projectView,
      searchParams,
      router,
      user,
      projectID,
      getProjectMetricsViewRequest,
      projectMetricsView,
    ],
  );

  const isEditable = useMemo(
    () => projectView.status !== INTERNAL_STATUS.deactivated,
    [projectView.status],
  );

  const isPublishable = useMemo(
    () => projectView.status === INTERNAL_STATUS.draft,
    [projectView.status],
  );

  const isDeactivatable = useMemo(
    () => projectView.status === INTERNAL_STATUS.active,
    [projectView.status],
  );

  const titleBarMenuProps = {
    items: [
      {
        key: TITLE_BAR_MENU_ITEM_KEYS.EDIT,
        label: 'Edit',
        icon: <EditOutlined style={{ fontSize: 20 }} />,
        show: isEditable,
      },
      {
        key: TITLE_BAR_MENU_ITEM_KEYS.DUPLICATE,
        label: 'Duplicate',
        icon: <CopyOutlined style={{ fontSize: 20 }} />,
        show: true,
      },
      {
        key: TITLE_BAR_MENU_ITEM_KEYS.PUBLISH,
        label: 'Publish',
        icon: <UploadUniversal style={{ fontSize: 20 }} />,
        show: isPublishable,
      },
      {
        key: TITLE_BAR_MENU_ITEM_KEYS.DEACTIVATE,
        label: 'Deactivate',
        icon: <StopUniversal style={{ fontSize: 20 }} />,
        danger: true,
        show: isDeactivatable,
      },
    ].filter((item) => item.show),
    onClick: ({ key }) => {
      switch (key) {
        case TITLE_BAR_MENU_ITEM_KEYS.EDIT:
          router.push(UCD_ROUTES_LINK.PROJECT_EDIT(projectID));
          break;
        case TITLE_BAR_MENU_ITEM_KEYS.DUPLICATE:
          router.push(UCD_ROUTES_LINK.PROJECT_DUPLICATE(projectID));
          break;
        case TITLE_BAR_MENU_ITEM_KEYS.PUBLISH:
          setStatusRequestType(INTERNAL_STATUS.active);
          dispatch(
            updateUCDProjectStatus(
              INTERNAL_STATUS.active,
              user.email,
              projectID,
            ),
          );
          updateStatusRequestRef.current = updateProjectStatusRequest;
          break;
        case TITLE_BAR_MENU_ITEM_KEYS.DEACTIVATE:
          Modal.confirm({
            title: `Deactivate "${projectView.project.name}" ? `,
            okText: 'Deactivate',
            okType: 'danger',
            content:
              'All campaigns under this project will also be deactivated. Once a project is deactivated, it can NOT be activated any more.',
            onOk: () => {
              setStatusRequestType(INTERNAL_STATUS.deactivated);
              dispatch(
                updateUCDProjectStatus(
                  INTERNAL_STATUS.deactivated,
                  user.email,
                  projectID,
                ),
              );
            },
          });
          break;
        default:
          break;
      }
    },
  };

  const [activeKey, setActiveKey] = useState('overview');
  const extraContent = useMemo(
    () =>
      activeKey === 'campaigns' &&
      projectView.campaignCount > 0 && (
        <CreateCampaignProjectButton
          router={router}
          project={projectView}
          showProject={false}
        />
      ),
    [
      projectView,
      updateProjectStatusRequest,
      statusRequestType,
      dispatch,
      user.email,
      projectID,
    ],
  );

  const isLoadingPage =
    isRequestsPending(getProjectRequest) || !countries || !user;

  return (
    <ProgressiveContainer isLoading={isLoadingPage}>
      <Flex align="center" justify="flex-start" vertical={false} gap={8}>
        <Button
          type="text"
          onClick={() => handleBackButton(props.previousPageIsListing)}
        >
          <ArrowLeftUniversal style={{ fontSize: 20 }} />
        </Button>
        <FolderOutlined style={{ fontSize: 20 }} />
        <Typography.Title
          style={{
            display: 'contents',
          }}
          level={4}
        >
          {projectView.project.name}
        </Typography.Title>
        <Dropdown menu={titleBarMenuProps}>
          <Button
            icon={<MoreVerticalUniversal />}
            shape="circle"
            size="small"
            type="text"
          />
        </Dropdown>
      </Flex>
      <Divider style={{ margin: '16px 0px 0px 0px' }} />

      <Tabs
        animated
        items={tabs}
        size="middle"
        style={{ width: '100%', padding: '4px 0px' }}
        tabBarExtraContent={extraContent}
        activeKey={activeKey}
        onChange={(key) => {
          setActiveKey(key);
          if (key === 'performance' && !isPerformanceTabClicked) {
            setIsPerformanceTabClicked(true);
            scribe.sendEvent(ScribeEventName.PROJECT_METRICS, {
              projectID,
              ...ScribeExtras.OPEN_PAGE,
            });
          }
        }}
        type="line"
      />
    </ProgressiveContainer>
  );
};

export default ProjectDetailPage;
